// RamsTemplateBuilder — three-pane editor for one RAMS questionnaire template version.
// Left: sections. Center: question list. Right: question properties + conditional logic.
// Editing a published version is blocked server-side — "Edit" here creates a new
// draft version first (immutable-once-published rule), then edits happen on that draft.
import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, Plus, Trash2, ChevronUp, ChevronDown, Save, CheckCircle, X, Loader2,
    GitBranch, Eye, History,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import RamsQuestionEditor from "@/components/rams/RamsQuestionEditor";
import RamsPreview from "@/components/rams/RamsPreview";

const CAN_EDIT_ROLES = new Set(["director", "administrator", "consultant"]);

const newQuestion = () => ({
    id: `q${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    field_key: `field_${Date.now() % 100000}`, label: "", question_type: "text", required: false,
    help_text: "", placeholder: "", client_guidance: "", reviewer_guidance: "", default_value: "",
    options: [], populate_from: null, ai_visible: true, doc_sections: [], conditional_rules: [], group_questions: [],
});
const newSection = () => ({ id: `s${Date.now()}`, title: "New section", order: 0, questions: [] });

export default function RamsTemplateBuilder() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const canEdit = user && CAN_EDIT_ROLES.has(user.role);

    const [template, setTemplate] = useState(null);
    const [version, setVersion] = useState(null);
    const [sections, setSections] = useState([]);
    const [activeSectionIdx, setActiveSectionIdx] = useState(0);
    const [activeQuestionId, setActiveQuestionId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [creatingVersion, setCreatingVersion] = useState(false);
    const [preview, setPreview] = useState(false);
    const [versionsOpen, setVersionsOpen] = useState(false);
    const [versionsList, setVersionsList] = useState([]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: tpl } = await api.get(`/rams/templates/${id}`);
            setTemplate(tpl);
            const { data: vlist } = await api.get(`/rams/templates/${id}/versions`);
            setVersionsList(vlist);
            const draft = vlist.find((v) => v.status === "draft");
            const toShow = draft || vlist.find((v) => v.id === tpl.published_version_id) || vlist[0];
            if (toShow) {
                const { data: full } = await api.get(`/rams/templates/${id}/versions/${toShow.id}`);
                setVersion(full);
                setSections(full.sections || []);
            }
        } catch (e) {
            toast.error("Could not load template");
            navigate("/app/rams/templates");
        }
        setLoading(false);
    }, [id, navigate]);

    useEffect(() => { load(); }, [load]);

    const readonly = !canEdit || (version && version.status !== "draft");

    const save = useCallback(async (sectionsToSave, silent = false) => {
        if (!version || version.status !== "draft") return;
        setSaving(true);
        try {
            await api.patch(`/rams/templates/${id}/versions/${version.id}`, {
                sections: (sectionsToSave || sections).map((s, i) => ({ ...s, order: i })),
            });
            if (!silent) toast.success("Saved");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
        }
        setSaving(false);
    }, [id, version, sections]);

    const startNewVersion = async () => {
        setCreatingVersion(true);
        try {
            const { data } = await api.post(`/rams/templates/${id}/versions`, null, {
                params: { source_version_id: version.id },
            });
            toast.success(`Draft v${data.version_number} created — you can now edit`);
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not create new version");
        }
        setCreatingVersion(false);
    };

    const publish = async () => {
        setPublishing(true);
        try {
            await save(sections, true);
            const { data } = await api.post(`/rams/templates/${id}/versions/${version.id}/publish`);
            toast.success(`v${data.version_number} published`);
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Publish failed");
        }
        setPublishing(false);
    };

    // Section ops
    const addSection = () => {
        const s = newSection();
        const next = [...sections, s];
        setSections(next);
        setActiveSectionIdx(next.length - 1);
        setActiveQuestionId(null);
    };
    const removeSection = (idx) => {
        if (sections.length === 1) return toast.error("Keep at least one section");
        const next = sections.filter((_, i) => i !== idx);
        setSections(next);
        setActiveSectionIdx(Math.min(idx, next.length - 1));
        setActiveQuestionId(null);
    };
    const moveSection = (idx, dir) => {
        const next = [...sections];
        const j = idx + dir;
        if (j < 0 || j >= next.length) return;
        [next[idx], next[j]] = [next[j], next[idx]];
        setSections(next);
        setActiveSectionIdx(j);
    };
    const updateSectionTitle = (idx, title) =>
        setSections((s) => s.map((sec, i) => (i === idx ? { ...sec, title } : sec)));

    // Question ops
    const activeSection = sections[activeSectionIdx];
    const addQuestion = () => {
        if (!activeSection) return;
        const qn = newQuestion();
        setSections((s) => s.map((sec, i) =>
            i === activeSectionIdx ? { ...sec, questions: [...(sec.questions || []), qn] } : sec));
        setActiveQuestionId(qn.id);
    };
    const updateQuestion = (qid, patch) => {
        setSections((s) => s.map((sec, i) => {
            if (i !== activeSectionIdx) return sec;
            return { ...sec, questions: (sec.questions || []).map((q) => (q.id === qid ? { ...q, ...patch } : q)) };
        }));
    };
    const deleteQuestion = (qid) => {
        setSections((s) => s.map((sec, i) =>
            i !== activeSectionIdx ? sec : { ...sec, questions: (sec.questions || []).filter((q) => q.id !== qid) }));
        setActiveQuestionId(null);
    };
    const moveQuestion = (qid, dir) => {
        setSections((s) => s.map((sec, i) => {
            if (i !== activeSectionIdx) return sec;
            const qs = [...(sec.questions || [])];
            const idx = qs.findIndex((q) => q.id === qid);
            const j = idx + dir;
            if (j < 0 || j >= qs.length) return sec;
            [qs[idx], qs[j]] = [qs[j], qs[idx]];
            return { ...sec, questions: qs };
        }));
    };

    const activeQuestion = activeSection?.questions?.find((q) => q.id === activeQuestionId) || null;
    const allQuestionsFlat = sections.flatMap((s) => s.questions || []);

    if (loading) {
        return <div className="p-10 text-center text-[#9A968F]"><Loader2 className="inline animate-spin mr-2" size={16} />Loading questionnaire…</div>;
    }
    if (!template || !version) return <div className="p-10 text-center text-[#9A968F]">Template not found.</div>;

    const isPublished = version.status === "published";
    const isArchived = version.status === "archived";

    return (
        <div data-testid="rams-template-builder" className="flex flex-col -mx-6 -my-6" style={{ minHeight: "calc(100vh - 64px)" }}>
            <div className="flex items-center gap-3 px-5 py-3 border-b border-[#E5E0DA] bg-white">
                <button data-testid="rams-builder-back" onClick={() => navigate("/app/rams/templates")}
                    className="p-1.5 rounded hover:bg-[#F6F4F0] text-[#6F6A64]"><ArrowLeft size={16} /></button>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-[#1F1F1F] truncate">{template.name}</span>
                        <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold uppercase"
                            style={{
                                background: isPublished ? "#E8F1E6" : isArchived ? "#F0EDE8" : "#FCF1DC",
                                color: isPublished ? "#3F7D3A" : isArchived ? "#6F6A64" : "#C68A1C",
                            }}>{version.status}</span>
                        <span className="text-[11px] text-[#9A968F]">v{version.version_number}</span>
                    </div>
                </div>
                <button data-testid="rams-builder-versions" onClick={() => setVersionsOpen(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold rounded-lg border border-[#E5E0DA] text-[#1F1F1F] hover:bg-[#F6F4F0]">
                    <History size={13} /> Versions
                </button>
                <button data-testid="rams-builder-preview" onClick={() => setPreview(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold rounded-lg border border-[#E5E0DA] text-[#1F1F1F] hover:bg-[#F6F4F0]">
                    <Eye size={13} /> Preview as client
                </button>
                {canEdit && readonly && (
                    <button data-testid="rams-builder-new-version" onClick={startNewVersion} disabled={creatingVersion}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold rounded-lg text-white disabled:opacity-60"
                        style={{ background: "var(--brand-accent, #C75A1B)" }}>
                        {creatingVersion ? <Loader2 size={13} className="animate-spin" /> : <GitBranch size={13} />} Create new version to edit
                    </button>
                )}
                {canEdit && !readonly && (
                    <>
                        <button data-testid="rams-builder-save" onClick={() => save()} disabled={saving}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold rounded-lg border border-[#E5E0DA] text-[#1F1F1F] hover:bg-[#F6F4F0] disabled:opacity-60">
                            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
                        </button>
                        <button data-testid="rams-builder-publish" onClick={publish} disabled={publishing}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold rounded-lg text-white disabled:opacity-60"
                            style={{ background: "#3F7D3A" }}>
                            {publishing ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle size={13} />} Publish
                        </button>
                    </>
                )}
            </div>

            <div className="flex flex-1 overflow-hidden">
                <div className="w-56 border-r border-[#E5E0DA] bg-[#F5F3F0] flex flex-col">
                    <div className="px-3 py-2.5 border-b border-[#E5E0DA] flex items-center justify-between">
                        <span className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64]">Sections</span>
                        {!readonly && (
                            <button data-testid="rams-builder-add-section" onClick={addSection}
                                className="p-1 rounded hover:bg-[#F0EDE8] text-[#6F6A64]"><Plus size={14} /></button>
                        )}
                    </div>
                    <div className="flex-1 overflow-y-auto py-2 px-2 space-y-1">
                        {sections.map((sec, idx) => (
                            <div key={sec.id} data-testid={`rams-builder-section-${idx}`}
                                className={`group flex items-center gap-1.5 px-2 py-2 rounded-lg cursor-pointer ${activeSectionIdx === idx ? "bg-white border border-[#E5E0DA] shadow-sm" : "hover:bg-white/70"}`}
                                onClick={() => { setActiveSectionIdx(idx); setActiveQuestionId(null); }}>
                                <span className="w-5 h-5 rounded text-[10px] font-bold flex-shrink-0 grid place-items-center text-white"
                                    style={{ background: activeSectionIdx === idx ? "var(--brand-accent, #C75A1B)" : "#C0BCB4" }}>{idx + 1}</span>
                                <span className="text-[12px] text-[#1F1F1F] flex-1 truncate font-semibold">{sec.title}</span>
                                {!readonly && (
                                    <div className="flex gap-0 opacity-0 group-hover:opacity-100">
                                        <button onClick={(e) => { e.stopPropagation(); moveSection(idx, -1); }} disabled={idx === 0}
                                            className="p-0.5 hover:bg-[#F0EDE8] rounded disabled:opacity-30"><ChevronUp size={11} /></button>
                                        <button onClick={(e) => { e.stopPropagation(); moveSection(idx, 1); }} disabled={idx === sections.length - 1}
                                            className="p-0.5 hover:bg-[#F0EDE8] rounded disabled:opacity-30"><ChevronDown size={11} /></button>
                                        <button onClick={(e) => { e.stopPropagation(); removeSection(idx); }}
                                            className="p-0.5 hover:bg-red-50 rounded text-[#9A968F] hover:text-red-600"><X size={11} /></button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto bg-[#F5F3F0]/40">
                    {activeSection ? (
                        <div className="p-6 max-w-2xl mx-auto">
                            <div className="mb-4">
                                <input data-testid="rams-builder-section-title" value={activeSection.title} disabled={readonly}
                                    onChange={(e) => updateSectionTitle(activeSectionIdx, e.target.value)}
                                    className="text-lg font-bold text-[#1F1F1F] bg-transparent border-b-2 border-transparent focus:border-[var(--brand-accent,_#C75A1B)] focus:outline-none w-full pb-1" />
                                <p className="text-[11px] text-[#9A968F] mt-0.5">
                                    {(activeSection.questions || []).length} question{(activeSection.questions || []).length !== 1 ? "s" : ""}
                                </p>
                            </div>
                            <div className="space-y-2 mb-4">
                                {(activeSection.questions || []).length === 0 && (
                                    <div className="py-10 text-center border-2 border-dashed border-[#E5E0DA] rounded-xl text-[12px] text-[#9A968F]">
                                        No questions yet — add one below
                                    </div>
                                )}
                                {(activeSection.questions || []).map((qn, idx) => (
                                    <QuestionRow key={qn.id} question={qn}
                                        isSelected={activeQuestionId === qn.id}
                                        onSelect={() => setActiveQuestionId(qn.id)}
                                        onMoveUp={() => moveQuestion(qn.id, -1)}
                                        onMoveDown={() => moveQuestion(qn.id, 1)}
                                        isFirst={idx === 0} isLast={idx === (activeSection.questions || []).length - 1}
                                        readonly={readonly} />
                                ))}
                            </div>
                            {!readonly && (
                                <button data-testid="rams-builder-add-question" onClick={addQuestion}
                                    className="inline-flex items-center gap-1.5 text-[13px] font-semibold px-3 py-2 rounded-lg border border-dashed border-[#C0BCB4] text-[#6F6A64] hover:border-[var(--brand-accent,_#C75A1B)] hover:text-[var(--brand-accent,_#C75A1B)]">
                                    <Plus size={14} /> Add question
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="flex items-center justify-center h-full text-[#9A968F] text-sm">Add a section to start.</div>
                    )}
                </div>

                <div className="w-80 border-l border-[#E5E0DA] bg-white flex-shrink-0 flex flex-col overflow-y-auto">
                    <div className="px-4 py-2.5 border-b border-[#E5E0DA]">
                        <span className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64]">Question properties</span>
                    </div>
                    <RamsQuestionEditor question={activeQuestion} readonly={readonly} allQuestions={allQuestionsFlat}
                        onChange={(patch) => activeQuestion && updateQuestion(activeQuestion.id, patch)}
                        onDelete={() => activeQuestion && deleteQuestion(activeQuestion.id)} />
                </div>
            </div>

            {preview && <RamsPreview sections={sections} onClose={() => setPreview(false)} />}
            {versionsOpen && (
                <VersionsDialog versions={versionsList} currentId={version.id} templateId={id}
                    onClose={() => setVersionsOpen(false)}
                    onOpenVersion={async (vid) => {
                        setVersionsOpen(false);
                        const { data: full } = await api.get(`/rams/templates/${id}/versions/${vid}`);
                        setVersion(full);
                        setSections(full.sections || []);
                        setActiveSectionIdx(0);
                        setActiveQuestionId(null);
                    }} />
            )}
        </div>
    );
}

function QuestionRow({ question, isSelected, onSelect, onMoveUp, onMoveDown, isFirst, isLast, readonly }) {
    return (
        <div onClick={onSelect} data-testid={`rams-builder-question-${question.id}`}
            className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-all ${isSelected ? "border-[var(--brand-accent,_#C75A1B)] bg-[#FBEFE5]/40 shadow-sm" : "border-[#E5E0DA] bg-white hover:border-[#C0BCB4]"}`}>
            <div className="flex-1 min-w-0">
                <p className="text-[13px] font-semibold text-[#1F1F1F] truncate">
                    {question.label || <span className="text-[#9A968F] italic">Untitled question</span>}
                </p>
                <p className="text-[11px] text-[#9A968F]">
                    {question.question_type}{question.required && <span className="text-red-600 ml-1">*</span>}
                    {question.conditional_rules?.length > 0 && <span className="ml-1.5 text-[#2A5DB0]">· conditional</span>}
                </p>
            </div>
            {!readonly && (
                <div className="flex gap-0.5 flex-shrink-0">
                    <button onClick={(e) => { e.stopPropagation(); onMoveUp(); }} disabled={isFirst}
                        className="p-0.5 hover:bg-[#F0EDE8] rounded disabled:opacity-30"><ChevronUp size={13} /></button>
                    <button onClick={(e) => { e.stopPropagation(); onMoveDown(); }} disabled={isLast}
                        className="p-0.5 hover:bg-[#F0EDE8] rounded disabled:opacity-30"><ChevronDown size={13} /></button>
                </div>
            )}
        </div>
    );
}

function VersionsDialog({ versions, currentId, onClose, onOpenVersion }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" data-testid="rams-versions-dialog">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
                <div className="px-5 py-4 border-b border-[#E5E0DA] flex items-center justify-between">
                    <h3 className="text-base font-bold text-[#1F1F1F]">Version history</h3>
                    <button onClick={onClose} className="p-1 text-[#9A968F] hover:text-[#1F1F1F]"><X size={16} /></button>
                </div>
                <div className="p-3 space-y-1.5 max-h-96 overflow-y-auto">
                    {versions.map((v) => (
                        <button key={v.id} data-testid={`rams-version-row-${v.version_number}`}
                            onClick={() => onOpenVersion(v.id)}
                            className={`w-full flex items-center justify-between p-3 rounded-lg border text-left ${v.id === currentId ? "border-[var(--brand-accent,_#C75A1B)] bg-[#FBEFE5]/40" : "border-[#E5E0DA] hover:bg-[#F5F3F0]"}`}>
                            <div>
                                <p className="text-sm font-semibold text-[#1F1F1F]">Version {v.version_number}</p>
                                <p className="text-[11px] text-[#9A968F]">{v.question_count} questions · {v.section_count} sections</p>
                            </div>
                            <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold uppercase"
                                style={{
                                    background: v.status === "published" ? "#E8F1E6" : v.status === "archived" ? "#F0EDE8" : "#FCF1DC",
                                    color: v.status === "published" ? "#3F7D3A" : v.status === "archived" ? "#6F6A64" : "#C68A1C",
                                }}>{v.status}</span>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}
